import { Upload } from './files.js';
import { apiClientV2, unwrapV2Response } from './index.js';

// Folder the file service stores taxonomy thumbnails under. It adds an `images/`
// segment itself, so node.thumbnail keys look like
// `taxonomy-thumbnails/images/1790000000000-spruce.jpg`.
export const TAXONOMY_THUMBNAIL_FOLDER = 'taxonomy-thumbnails';

/**
 * Fetch the caller's taxonomy tree for one project type: system nodes plus the
 * active org's nodes (or the caller's personal nodes with no active org), each
 * flagged with `is_available` for that mode.
 *
 * v2 endpoint: `taxonomy/nodes?mode=<orthophoto|single_image>` (the `/v2` prefix is
 * part of the apiClientV2 base URL). Nodes link by `parent_uuid`; `scope` is
 * `system`, `org` or `user`.
 * Response shape: { success, message, data: { nodes: [...] }, errors }.
 *
 * @param {string} mode - A `PROJECT_MODE` value (required; the server 400s without it).
 */
export const getTaxonomyNodesForMode = async mode => {
	const { data, error } = await apiClientV2.get('/taxonomy/nodes', { mode });
	if (error) throw error;
	return unwrapV2Response(data)?.nodes ?? [];
};

/**
 * Create a taxonomy node under an existing parent. The backend queues model
 * training for it; the node joins the tree once that finishes.
 *
 * v2 endpoint: `POST taxonomy/nodes`.
 *
 * @param {object} payload
 * @param {string} payload.name
 * @param {string} payload.key - client-generated taxonomy key
 * @param {string} payload.parent_uuid
 * @param {string} payload.color - hex color
 * @param {string} [payload.description]
 * @param {string} [payload.trained_label] - parent label the object is trained from
 * @param {string} [payload.thumbnail] - storage key of an uploaded thumbnail
 */
export const createTaxonomyNode = async payload => {
	const { data, error } = await apiClientV2.post('/taxonomy/nodes', payload);
	if (error) throw error;
	return unwrapV2Response(data);
};

/** `Screenshot 2026-09-02 at 12.59.42\u202fPM.png` -> `1790000000000-screenshot-2026-09-02-at-12-59-42-pm.png` */
const thumbnailFileName = file => {
	const name = String(file?.name ?? 'thumbnail');
	const dot = name.lastIndexOf('.');
	const base = (dot > 0 ? name.slice(0, dot) : name)
		.normalize('NFKD')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
	const extension =
		dot > 0
			? name
					.slice(dot)
					.toLowerCase()
					.replace(/[^a-z0-9.]/g, '')
			: '';
	return `${Date.now()}-${base || 'thumbnail'}${extension}`;
};

/**
 * Upload a taxonomy thumbnail through the file service and answer with the
 * storage key to put in `createTaxonomyNode`'s `thumbnail`.
 *
 * Reuses the multipart `Upload` the project image upload is built on. The
 * service composes the key from `projectId` and the file name, so the
 * thumbnail folder goes in as the `projectId` — the only way the caller can
 * steer where the file lands.
 *
 * The file is renamed first: the service builds the storage key from the file
 * name, and a picked name may carry spaces or non-ASCII characters (macOS
 * screenshot names do) that make an awkward key. A timestamp keeps two users'
 * `image.png` apart.
 *
 * @param {File} file - the picked image
 * @param {(percent: number) => void} [onProgress]
 * @returns {Promise<string>} the storage key
 */
export const uploadTaxonomyThumbnail = async (file, onProgress = () => {}) => {
	const upload = new Upload({
		fileRef: new File([file], thumbnailFileName(file), { type: file.type }),
		projectId: TAXONOMY_THUMBNAIL_FOLDER,
		updateProgress: onProgress,
	});
	await upload.start();
	return upload.key;
};

/**
 * Update the editable fields of one of the caller's own taxonomy nodes.
 * System nodes are read-only and the tree offers no edit action for them.
 *
 * v2 endpoint: `PATCH taxonomy/nodes/{uuid}` — assumed to mirror the create
 * body; confirm the path and accepted fields with the backend.
 *
 * @param {string} uuid
 * @param {object} payload
 * @param {string} [payload.name]
 * @param {string} [payload.description]
 * @param {string} [payload.color] - hex colour
 * @param {string} [payload.thumbnail] - storage key of an uploaded thumbnail
 */
// TODO: not on the backend yet (only GET and POST exist); the edit button stays
// hidden behind OBJECT_ACTIONS_ENABLED in ObjectTreeCard until it is.
export const updateTaxonomyNode = async (uuid, payload) => {
	const { data, error } = await apiClientV2.patch(`/taxonomy/nodes/${uuid}`, payload);
	if (error) throw error;
	return unwrapV2Response(data);
};
